import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AuthLayout from '../components/AuthLayout';
import assetService, { Asset, AssetParams } from '../services/asset.service';
import clientService from '../services/client.service';
import categoryService, { Category } from '../services/category.service';

const AssetList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientIdFromQuery = queryParams.get('clientId');
  
  const [assets, setAssets] = useState<Asset[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalAssets, setTotalAssets] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>(clientIdFromQuery || '');
  const [activeFilter, setActiveFilter] = useState<string>('true');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchAssets = async (params: AssetParams = {}) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await assetService.getAssets({
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage,
        search: searchTerm,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
        client: clientFilter || undefined,
        isActive: activeFilter ? activeFilter === 'true' : undefined,
        ...params
      });
      
      setAssets(response.assets);
      setTotalAssets(response.pagination.total);
    } catch (err: any) {
      console.error('Error fetching assets:', err);
      setError('Falha ao carregar ativos. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await categoryService.getCategories(true);
      setCategories(response.categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
    fetchCategories();
  }, [page, rowsPerPage, searchTerm, categoryFilter, statusFilter, clientFilter, activeFilter]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleCategoryFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setCategoryFilter(event.target.value as string);
    setPage(0);
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setStatusFilter(event.target.value as string);
    setPage(0);
  };

  const handleActiveFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setActiveFilter(event.target.value as string);
    setPage(0);
  };

  const handleAddAsset = () => {
    navigate('/assets/new');
  };

  const handleEditAsset = (assetId: string) => {
    navigate(`/assets/edit/${assetId}`);
  };

  const handleViewAsset = (assetId: string) => {
    navigate(`/assets/${assetId}`);
  };

  const handleDeleteClick = (asset: Asset) => {
    setAssetToDelete(asset);
    setDeleteDialogOpen(true);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    if (!assetToDelete) return;
    
    try {
      setDeleteLoading(true);
      setDeleteError('');
      
      await assetService.deleteAsset(assetToDelete._id as string);
      
      setDeleteDialogOpen(false);
      setAssetToDelete(null);
      
      // Refresh asset list
      fetchAssets();
    } catch (err: any) {
      console.error('Error deleting asset:', err);
      setDeleteError('Falha ao excluir ativo. Por favor, tente novamente.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAssetToDelete(null);
    setDeleteError('');
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'maintenance':
        return 'Em Manutenção';
      case 'inactive':
        return 'Inativo';
      case 'retired':
        return 'Descontinuado';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'maintenance':
        return 'warning';
      case 'inactive':
        return 'default';
      case 'retired':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <AuthLayout title="Ativos">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Ativos
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddAsset}
            sx={{ 
              backgroundColor: '#2e7d32',
              '&:hover': {
                backgroundColor: '#1b5e20',
              }
            }}
          >
            Novo Ativo
          </Button>
        </Box>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Buscar"
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="category-filter-label">Categoria</InputLabel>
              <Select
                labelId="category-filter-label"
                value={categoryFilter}
                // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
                onChange={handleCategoryFilterChange}
                label="Categoria"
              >
                <MenuItem value="">Todas</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
                onChange={handleStatusFilterChange}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="active">Ativo</MenuItem>
                <MenuItem value="maintenance">Em Manutenção</MenuItem>
                <MenuItem value="inactive">Inativo</MenuItem>
                <MenuItem value="retired">Descontinuado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="active-filter-label">Disponibilidade</InputLabel>
              <Select
                labelId="active-filter-label"
                value={activeFilter}
                // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
                onChange={handleActiveFilterChange}
                label="Disponibilidade"
              >
                <MenuItem value="true">Disponíveis</MenuItem>
                <MenuItem value="false">Indisponíveis</MenuItem>
                <MenuItem value="">Todos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {clientIdFromQuery && (
            <Grid item xs={12} sm={6} md={2}>
              <Button 
                variant="outlined" 
                fullWidth 
                onClick={() => navigate('/assets')}
                sx={{ height: '40px' }}
              >
                Limpar Filtro de Cliente
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
      
      {error && (
        <Box sx={{ mb: 3 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Número de Série</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} sx={{ color: '#2e7d32' }} />
                  </TableCell>
                </TableRow>
              ) : assets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nenhum ativo encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                assets.map((asset) => (
                  <TableRow hover key={asset._id}>
                    <TableCell>{asset.name}</TableCell>
                    <TableCell>{asset.category}</TableCell>
                    <TableCell>
                      {typeof asset.client === 'object' && asset.client !== null
                        ? asset.client.name 
                        : asset.client || 'Sem cliente'}
                    </TableCell>
                    <TableCell>{asset.serialNumber || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(asset.status)} 
                        color={getStatusColor(asset.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Visualizar">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewAsset(asset._id as string)}
                          sx={{ color: '#2196f3' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditAsset(asset._id as string)}
                          sx={{ color: '#ff9800' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteClick(asset)}
                          sx={{ color: '#f44336' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalAssets}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o ativo "{assetToDelete?.name}"?
            Esta ação não pode ser desfeita.
          </DialogContentText>
          {deleteError && (
            <Box sx={{ mt: 2 }}>
              <Typography color="error">{deleteError}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            {deleteLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
};

export default AssetList;
